import axios, { Axios, AxiosRequestConfig } from "axios";
import { CURRENT_TENANT_KEY, mapAuthSessionToUser } from "./useAuth";
import env from "src/utils/env";
import { fetchAuthSession } from "aws-amplify/auth";

interface Fetcher extends Axios {
  get: <T>(url: string, config?: AxiosRequestConfig<any>) => Promise<T>;
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig<any>) => Promise<T>;
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig<any>) => Promise<T>;
  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig<any>) => Promise<T>;
  delete: <T>(url: string, config?: AxiosRequestConfig<any>) => Promise<T>;
  options: <T>(url: string, config?: AxiosRequestConfig<any>) => Promise<T>;
}

const fetcher = axios.create({
  baseURL: env.QUANT_API_URL,
});

fetcher.interceptors.response.use(r => r.data);
fetcher.interceptors.request.use(async r => {
  const authSession = await fetchAuthSession();

  if (authSession.tokens) {
    const user = mapAuthSessionToUser(authSession);
    const currentTenantId = localStorage.getItem(CURRENT_TENANT_KEY) ?? user?.tenantIds[0];

    r.headers["Authorization"] = `Bearer ${user.tokens.idToken}`;
    r.headers["x-tenant-id"] = currentTenantId;
  }

  return r;
});

export const endpoints = {
  tokens: {
    all: "/tokens",
    by_id: (token_id: string) => `/tokens/${token_id}`,
  },
  blockchains: {
    all: "/blockchains",
    by_id: (blockchain_id: string) => `/blockchains/${blockchain_id}`,
  },
  strategies: {
    all: "/strategies",
    by_id: (strategy_id: string) => `/strategies/${strategy_id}`,
    catalog: {
      integration_request: "/strategies/catalog/integration_request",
    },
  },
  portfolio_overview: {
    metrics: "/portfolio_overview/metrics",
  },
  positions: {
    all: "/positions",
    by_id: {
      metrics: (position_id: string) => `/positions/${position_id}/metrics?refresh=true`,
      management_events: (position_id: string) => `/positions/${position_id}/management_events`,
      returns_snapshots: (position_id: string) => `/positions/${position_id}/returns_snapshots`,
      last_updated: (position_id: string) => `/positions/${position_id}/last_updated`,
    },
  },
  staking: {
    validators: {
      kpis: "/staking/validators/kpis",
      eth: "/staking/validators/eth",
    },
  },
  metadata: {
    asset_categories: "/metadata/asset_categories",
  },
  user: {
    tenants: "/user/tenants",
  },
  discovery: {
    all: "/discovery",
    apy: `/discovery/apy`,
    capacity: `/discovery/capacity`,
    integration_request: `/discovery/integration_request`,
  },
  risk_radar: {
    proxy: (path: string) => `/risk_radar${path}`
  },
  admin: {
    alarms: "/admin/alarms",
  },
};

export const useApi = () => fetcher as Fetcher;
